import { fetch_requestor_application_forms } from '@/services/requestor_portal_service';
import { fetch_assign_form_status_data, fetch_available_application_transitions, fetch_form_workflow_transition_rules } from '@/services/workflow_service';
import { application_form_id, application_form_record_id, error_message, normalize_assigned_form_status, normalize_transition_rule, to_array, unique_statuses } from './view_case_details_helpers';

export default {
  demo_decision_workflow() {
    const statuses = demo_statuses();
    return decision_workflow(statuses, demo_transitions(statuses));
  },
  async load_decision_workflow() {
    reset_decision_workflow(this);

    try {
      await load_workflow_for_context(this);
    } catch (error) {
      this.decision_error = error_message(error, 'Failed to load workflow statuses.');
    } finally {
      this.loading_decision_workflow = false;
    }
  },
  async open_decision_modal() {
    this.show_decision_modal = true;
    this.decision_reason = '';
    await this.load_decision_workflow();
  },
};

function demo_statuses() {
  return [
    { id: 1, name: 'In review', order: 1, is_final: false },
    { id: 2, name: 'Pending evidence', order: 2, is_final: false },
    { id: 3, name: 'In progress', order: 3, is_final: false },
    { id: 4, name: 'Closed', order: 4, is_final: true },
  ];
}

function decision_workflow(statuses = [], transitions = []) {
  return {
    statuses,
    transitions,
  };
}

function demo_transitions(statuses = []) {
  return statuses.slice(0, -1).map((status, index) =>
    demo_transition(status, statuses[index + 1])
  );
}

function demo_transition(status = {}, next_status = {}) {
  return {
    from_status_id: status.id,
    to_status_id: next_status.id,
    is_active: true,
  };
}

function reset_decision_workflow(component) {
  component.loading_decision_workflow = true;
  component.decision_error = '';
  component.workflow_statuses = [];
  component.workflow_transition_rules = [];
  component.backend_available_transitions = [];
  component.selected_decision_status_id = '';
  component.decision_application_form_id = null;
}

async function load_workflow_for_context(component) {
  if (!component.selected_application_id) {
    apply_demo_workflow(component);
    return;
  }

  await load_backend_workflow(component);
}

function apply_demo_workflow(component) {
  const demo_workflow = component.demo_decision_workflow();
  component.workflow_statuses = demo_workflow.statuses;
  component.workflow_transition_rules = demo_workflow.transitions;
  set_default_decision_status(component);
}

async function load_backend_workflow(component) {
  const [application_forms, available_transition_response] = await Promise.all([
    fetch_requestor_application_forms(component.selected_application_id),
    fetch_available_application_transitions({ application_id: component.selected_application_id }),
  ]);
  const available_transitions = available_transitions_from_response(available_transition_response);
  component.backend_available_transitions = available_transitions;
  const current_status_name = available_transition_response?.data?.current_status?.name
    || available_transition_response?.current_status?.name;
  if (current_status_name) {
    component.apply_case_status(current_status_name);
  }
  component.decision_application_form_id = application_form_record_id(application_forms[0]);
  const form_ids = unique_form_ids(application_forms);

  if (!form_ids.length) {
    component.decision_error = 'No workflow form was found for this case.';
    return;
  }

  await apply_backend_workflow(component, form_ids, available_transitions);
}

function unique_form_ids(application_forms = []) {
  return [...new Set(application_forms.map(application_form_id).filter(Boolean))];
}

async function apply_backend_workflow(component, form_ids = [], available_transitions = []) {
  component.workflow_statuses = await workflow_statuses_for_forms(form_ids);
  const configured_rules = await transition_rules_for_statuses(component.workflow_statuses, form_ids[0]);
  component.workflow_transition_rules = available_transitions.length
    ? available_transitions.map((transition) => ({
      ...transition,
      from_status_id: transition.current_status_id,
      to_status_id: transition.to_status?.id,
      is_active: true,
    }))
    : configured_rules;
  set_default_decision_status(component);
  set_empty_workflow_message(component);
}

function available_transitions_from_response(response = {}) {
  const data = response?.data || response;
  // Transition rules use AssignedFormStatus IDs. Keep the explicit fallback
  // for older API responses that only exposed current_status.id.
  const current_status_id = Number(
    data?.current_status?.assigned_form_status_id
      || data?.current_status?.id
  ) || null;
  return (Array.isArray(data?.available_transitions) ? data.available_transitions : [])
    .filter((transition) => transition?.to_status?.id)
    .map((transition) => ({ ...transition, current_status_id }));
}

async function workflow_statuses_for_forms(form_ids = []) {
  const status_groups = await Promise.all(form_ids.map(statuses_for_form));
  return unique_statuses(status_groups.flat()).filter((status) => status.id && status.name);
}

async function statuses_for_form(form_id) {
  const response = await fetch_assign_form_status_data({ form_id });
  return assigned_statuses_from_response(response).map((status, index) =>
    assigned_status_for_form(status, index, form_id)
  );
}

function assigned_status_for_form(status = {}, index = 0, form_id = null) {
  return {
    ...normalize_assigned_form_status(status, index),
    form_id,
  };
}

function assigned_statuses_from_response(response) {
  return to_array(first_present([
    response?.assigned_statuses,
    response?.data?.assigned_statuses,
    response?.selected_statuses,
    response?.data?.selected_statuses,
    [],
  ]));
}

async function transition_rules_for_statuses(statuses = [], fallback_form_id) {
  const transition_groups = await Promise.all(
    statuses.map((status) => transition_rules_for_status(status, fallback_form_id))
  );
  return transition_groups.flat().filter((rule) => rule.from_status_id && rule.to_status_id);
}

async function transition_rules_for_status(status, fallback_form_id) {
  const response = await fetch_form_workflow_transition_rules({
    form_id: status.form_id || fallback_form_id,
    from_status_id: status.id,
  });
  return transition_rules_from_response(response).map(normalize_transition_rule);
}

function transition_rules_from_response(response) {
  return to_array(first_present([
    response?.transition_rules,
    response?.data?.transition_rules,
    response?.rules,
    response?.data?.rules,
    response?.data,
    response,
  ]));
}

function first_present(values = []) {
  return values.find((value) => value !== undefined && value !== null);
}

function set_default_decision_status(component) {
  component.selected_decision_status_id = String(component.valid_next_statuses[0]?.id || '');
}

function set_empty_workflow_message(component) {
  if (!component.workflow_statuses.length) {
    component.decision_error = 'No statuses are configured for this case workflow.';
  }
}
